'use client'

import type { StudyDeckItem } from '@/types/study'
import { Badge } from '@/components/ui/badge'
import { MutedText } from '@/components/ui/typography'
import {
  IconAlertCircle,
  IconBook,
  IconEdit,
  IconMessageCircle,
  IconTrendingUp,
  IconX
} from '@tabler/icons-react'

interface CollectionItemsTableProps {
  items: StudyDeckItem[]
  onEdit?: (item: StudyDeckItem) => void
  onRemove?: (item: StudyDeckItem) => void
  isRemoving?: boolean
  locale?: string
}

function getStateLabel(state: number): string {
  if (state === 0) return 'New'
  if (state === 1) return 'Learning'
  if (state === 2) return 'Review'
  if (state === 3) return 'Relearning'
  return 'Unknown'
}

function getStateVariant(
  state: number
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (state === 0) return 'outline'
  if (state === 1) return 'default'
  if (state === 2) return 'secondary'
  if (state === 3) return 'default'
  return 'outline'
}

function itemSubtitle(item: StudyDeckItem) {
  if (item.note_type === 'sentence') {
    const title = item.source?.title
    return typeof title === 'string' && title
      ? `Sentence · ${title}`
      : 'Sentence'
  }

  const fields = item.fields || {}
  const pinyin = typeof fields.pinyin === 'string' ? fields.pinyin : ''
  const definition = Array.isArray(fields.definitions)
    ? fields.definitions[0]
    : item.back
  return [pinyin, definition].filter(Boolean).join(' · ')
}

export function CollectionItemsTable({
  items,
  onEdit,
  onRemove,
  isRemoving = false,
  locale
}: CollectionItemsTableProps) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-muted/50 border-b">
            <th className="text-muted-foreground px-4 py-3 text-left text-xs font-semibold tracking-wider uppercase">
              Card
            </th>
            <th className="text-muted-foreground px-4 py-3 text-left text-xs font-semibold tracking-wider uppercase">
              Due
            </th>
            <th className="text-muted-foreground px-4 py-3 text-left text-xs font-semibold tracking-wider uppercase">
              Reviews
            </th>
            <th className="text-muted-foreground px-4 py-3 text-left text-xs font-semibold tracking-wider uppercase">
              Status
            </th>
            {(onEdit || onRemove) && (
              <th className="text-muted-foreground px-4 py-3 text-right text-xs font-semibold tracking-wider uppercase">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((item) => {
            const dueText =
              item.due && !Number.isNaN(new Date(item.due).getTime())
                ? new Date(item.due).toLocaleDateString(locale || undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })
                : null

            return (
              <tr key={item.card_id} className="transition-colors">
                <td className="px-4 py-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="bg-muted text-muted-foreground mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                      {item.note_type === 'sentence' ? (
                        <IconMessageCircle className="h-4 w-4" />
                      ) : (
                        <IconBook className="h-4 w-4" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <MutedText className="max-w-xl text-sm font-medium">
                        {item.front}
                      </MutedText>
                      <MutedText className="text-muted-foreground/70 mt-1 line-clamp-2 max-w-xl text-xs">
                        {itemSubtitle(item) || 'Recognition card'}
                      </MutedText>
                    </div>
                  </div>
                </td>

                <td className="px-4 py-3">
                  {dueText ? (
                    <span className="text-foreground text-sm">{dueText}</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </td>

                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <IconTrendingUp className="text-muted-foreground h-3.5 w-3.5" />
                    <span className="text-muted-foreground text-sm">
                      {item.reps}
                    </span>
                    {item.lapses > 0 && (
                      <Badge
                        variant="outline"
                        size="sm"
                        className="ml-1 gap-1 border-amber-500/30 bg-amber-500/10 text-xs text-amber-700 dark:text-amber-400"
                      >
                        <IconAlertCircle className="h-3 w-3" />
                        {item.lapses}
                      </Badge>
                    )}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <Badge
                    variant={getStateVariant(item.state)}
                    size="sm"
                    className="text-xs"
                  >
                    {getStateLabel(item.state)}
                  </Badge>
                </td>

                {(onEdit || onRemove) && (
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {onEdit && (
                        <button
                          onClick={(event) => {
                            event.stopPropagation()
                            onEdit(item)
                          }}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          title="Edit card"
                        >
                          <IconEdit className="h-4 w-4" />
                        </button>
                      )}
                      {onRemove && (
                        <button
                          onClick={(event) => {
                            event.stopPropagation()
                            onRemove(item)
                          }}
                          disabled={isRemoving}
                          className="text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                          title="Remove from deck"
                        >
                          <IconX className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
